import * as Yup from "yup";
import PublicLayout from "sections/PublicLayout";
import SteppedForm from "sections/public/SteppedForm";
import { submitJobData } from "firestoreService";
import {
  APPLICANT_ID_MAX_BYTES,
  APPLICANT_PDF_MAX_BYTES,
  APPLICANT_PHOTO_MAX_BYTES,
  DOCUMENT_TYPES,
  IMAGE_TYPES,
  applicantIdLimit,
  formatBytes,
} from "utils/uploads";

const STEPS = [
  {
    label: "About you",
    description: "Your basic details, so we know who we are speaking to.",
    fields: [
      { name: "name", label: "Full name", type: "text", required: true, maxLength: 120 },
      { name: "fatherName", label: "Father's name", type: "text", required: true, maxLength: 120 },
      { name: "email", label: "Email address", type: "email", required: true, maxLength: 200 },
      { name: "mobileNo", label: "Mobile number", type: "tel", required: true, maxLength: 10 },
      {
        name: "alternateMobileNo",
        label: "Alternate mobile number",
        type: "tel",
        required: true,
        maxLength: 10,
      },
      { name: "age", label: "Age", type: "number", required: true },
      {
        name: "gender",
        label: "Gender",
        type: "radio",
        required: true,
        options: [
          { value: "female", label: "Female" },
          { value: "male", label: "Male" },
        ],
      },
      {
        name: "maritalStatus",
        label: "Marital status",
        type: "radio",
        required: true,
        options: [
          { value: "married", label: "Married" },
          { value: "unmarried", label: "Unmarried" },
        ],
      },
    ],
  },
  {
    label: "Addresses",
    description: "Where you live now, and your permanent address.",
    fields: [
      {
        name: "currentAddress",
        label: "Current address",
        type: "textarea",
        required: true,
        full: true,
        maxLength: 500,
      },
      {
        name: "permanentAddress",
        label: "Permanent address",
        type: "textarea",
        required: true,
        full: true,
        maxLength: 500,
      },
      {
        name: "prefferedArea",
        label: "Preferred area of work",
        type: "textarea",
        required: true,
        full: true,
        maxLength: 500,
      },
    ],
  },
  {
    label: "Work",
    description: "What you have done, and what you are looking for.",
    fields: [
      {
        name: "qualification",
        label: "Highest qualification",
        type: "text",
        required: true,
        maxLength: 200,
      },
      { name: "experience", label: "Experience (in years)", type: "number", required: true },
      {
        name: "profile",
        label: "Profile / job type",
        type: "text",
        required: true,
        maxLength: 200,
      },
      {
        name: "salaryExpected",
        label: "Expected salary",
        type: "text",
        required: true,
        maxLength: 50,
      },
      { name: "referral", label: "Referred by (optional)", type: "text", maxLength: 200 },
    ],
  },
  {
    label: "Documents",
    description:
      "A clear photo and one ID proof. Both stay private to our team. The date of registration is recorded automatically when you submit.",
    fields: [
      { name: "photo", label: "Your photo", type: "photo", required: true, full: true },
      {
        name: "idProof",
        label: "ID proof (Aadhaar, PAN, voter ID or similar)",
        type: "document",
        required: true,
        full: true,
        helper:
          `png, jpg, jpeg, webp or pdf. Photos are reduced to ${formatBytes(
            APPLICANT_ID_MAX_BYTES
          )}. ` + `A PDF must be under ${formatBytes(APPLICANT_PDF_MAX_BYTES)}.`,
      },
    ],
  },
];

const validationSchema = Yup.object().shape({
  photo: Yup.mixed()
    .required("Photo is required")
    .test("fileType", "Unsupported file format (only png, jpg, jpeg, webp)", (value) =>
      !value ? true : IMAGE_TYPES.includes(value.type)
    )
    .test(
      "fileSize",
      `Photo is too large (max ${formatBytes(APPLICANT_PHOTO_MAX_BYTES)} after compression)`,
      (value) => !value || value.size <= APPLICANT_PHOTO_MAX_BYTES
    ),
  idProof: Yup.mixed()
    .required("ID proof is required")
    .test("fileType", "Unsupported file format (only png, jpg, jpeg, webp, pdf)", (value) =>
      !value ? true : DOCUMENT_TYPES.includes(value.type)
    )
    .test("fileSize", "ID proof is too large", function (value) {
      if (!value) return true;
      const limit = applicantIdLimit(value);
      if (value.size <= limit) return true;
      const kind = value.type === "application/pdf" ? "PDF" : "ID photo";
      return this.createError({
        message: `${kind} is too large (max ${formatBytes(limit)}${
          kind === "PDF" ? "" : " after compression"
        })`,
      });
    }),
  name: Yup.string().trim().max(120).required("Name is required"),
  fatherName: Yup.string().trim().max(120).required("Father's name is required"),
  email: Yup.string().trim().email("Invalid email format").max(200).required("Email is required"),
  age: Yup.number().positive("Age must be positive").required("Age is required"),
  gender: Yup.string().required("Gender is required"),
  mobileNo: Yup.string()
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
    .required("Mobile number is required"),
  alternateMobileNo: Yup.string()
    .matches(/^[0-9]{10}$/, "Alternate mobile number must be 10 digits")
    .required("Alternate mobile number is required"),
  experience: Yup.number()
    .min(0, "Experience must be at least 0")
    .required("Experience is required"),
  currentAddress: Yup.string().trim().max(500).required("Current address is required"),
  permanentAddress: Yup.string().trim().max(500).required("Permanent address is required"),
  qualification: Yup.string().trim().max(200).required("Qualification is required"),
  prefferedArea: Yup.string().trim().max(500).required("Preferred area is required"),
  maritalStatus: Yup.string().required("Marital status is required"),
  salaryExpected: Yup.string().trim().max(50).required("Expected salary is required"),
  profile: Yup.string().trim().max(200).required("Profile / job type is required"),
});

const initialValues = {
  photo: null,
  idProof: null,
  name: "",
  email: "",
  referral: "",
  age: "",
  gender: "",
  fatherName: "",
  mobileNo: "",
  alternateMobileNo: "",
  experience: "",
  currentAddress: "",
  permanentAddress: "",
  qualification: "",
  prefferedArea: "",
  maritalStatus: "",
  profile: "",
  salaryExpected: "",
};

function JobPlacement() {
  return (
    <PublicLayout
      eyebrow="A1 Job Placement"
      title="Job Placement Registration"
      subtitle="Tell us about your experience and we will match you with schools and families."
      sheet={false}
    >
      <SteppedForm
        steps={STEPS}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={submitJobData}
        successTitle="Registration received"
        successMessage="Thank you for registering. Our placement team will review your details and get in touch."
      />
    </PublicLayout>
  );
}

export default JobPlacement;
